// Port of src/app/components/nav-bar/services/input/search-input-helper.service.ts
// (SearchInputHelperService). Pure DOM/string helpers — ported as plain module
// functions (the Angular service was stateless). ElementRef parameters became
// HTMLElement; the DomSanitizer dependency is gone (htmlToPlainText takes the
// raw HTML string that Angular wrapped in SafeHtml).

/**
 * Check if input is a URL
 */
export function isUrl(input: string): boolean {
  if (input.includes('https')) {
    return true;
  }

  try {
    new URL(input);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate input length
 */
export function validateLength(input: string, maxLength: number): boolean {
  return input.length <= maxLength;
}

/**
 * Truncate text to max length and ensure cursor stays at end
 */
export function truncateAndUpdate(input: string, maxLength: number, editableDiv: HTMLElement): string {
  if (input.length > maxLength) {
    const truncated = input.substring(0, maxLength);
    editableDiv.innerText = truncated;
    moveCursorToEnd(editableDiv);
    return truncated;
  }
  return input;
}

/**
 * Add placeholder text to editable div
 */
export function addPlaceholder(editableDiv: HTMLDivElement, placeholder: string): void {
  const div = editableDiv;
  if (!div.innerText.trim()) {
    div.classList.add('placeholder');
    div.innerText = placeholder;
    div.blur();
  }
}

/**
 * Remove placeholder text from editable div
 */
export function removePlaceholder(editableDiv: HTMLDivElement, placeholder?: string): void {
  const div = editableDiv;
  if (div.classList.contains('placeholder')) {
    div.classList.remove('placeholder');
    // Only clear the content if it's actually the placeholder text or empty
    // This prevents clearing existing query content when clicking to edit
    if (!placeholder || div.innerText.trim() === placeholder || div.innerText.trim() === '') {
      div.innerText = '';
    }
  }
}

/**
 * Check if div has placeholder
 */
export function hasPlaceholder(editableDiv: HTMLDivElement): boolean {
  return editableDiv.classList.contains('placeholder');
}

/**
 * Move cursor to end of div
 */
export function moveCursorToEnd(div: HTMLElement): void {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(div);
  range.collapse(false);
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

/**
 * Get current cursor position in div
 */
export function getCursorPosition(div: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;

  const range = sel.getRangeAt(0);
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(div);
  preCaretRange.setEnd(range.endContainer, range.endOffset);

  return preCaretRange.toString().length;
}

/**
 * Set cursor position in div
 */
export function setCursorPosition(div: HTMLElement, position: number): void {
  const range = document.createRange();
  const sel = window.getSelection();

  if (!sel) return;

  const textNode = getTextNodeAtPosition(div, position);
  if (textNode) {
    range.setStart(textNode.node, textNode.offset);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

/**
 * Truncate file name to max length
 */
export function truncateFileName(fileName: string, maxLength: number = 15): string {
  if (!fileName) {
    return '';
  }
  if (fileName.length <= maxLength) {
    return fileName;
  }
  return fileName.substring(0, maxLength) + '...';
}

/**
 * Sanitize input text (strip HTML, trim whitespace)
 */
export function sanitizeInput(text: string): string {
  // Create a temporary div to strip HTML
  const temp = document.createElement('div');
  temp.innerHTML = text;
  return (temp.textContent || temp.innerText || '').trim();
}

/**
 * Set contenteditable attribute on element
 */
export function setContentEditable(element: HTMLElement, isEditable: boolean): void {
  element.setAttribute('contenteditable', isEditable.toString());
}

/**
 * Check if text contains only whitespace
 */
export function isEmpty(text: string): boolean {
  return !text || text.trim() === '';
}

/**
 * Extract plain text from prompter HTML (Angular took a SafeHtml).
 */
export function htmlToPlainText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html.toString(), 'text/html');

  if (doc.body.textContent) {
    return doc.body.textContent.split(': ')[1]?.trim()?.split('(see ')[0]?.trim() || '';
  }

  return '';
}

/**
 * Check if input matches forbidden words (used for prompter activation)
 */
export function containsForbiddenWords(input: string): boolean {
  const words = input.trim().split(/\s+/);
  const forbiddenWords = ['like', 'song', 'similar'];

  return !words.some((word) => forbiddenWords.includes(word.toLowerCase()));
}

/**
 * Insert text at cursor position
 */
export function insertTextAtCursor(div: HTMLElement, text: string): void {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    // Fallback: replace entire content
    div.textContent = text;
  }
}

/**
 * Get word count in text
 */
export function getWordCount(text: string): number {
  return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
}

/**
 * Check if text should trigger prompter mode
 */
export function shouldActivatePrompter(text: string): boolean {
  const wordCount = getWordCount(text);
  return wordCount > 0 && containsForbiddenWords(text);
}

/**
 * Escape HTML special characters
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Unescape HTML entities
 */
export function unescapeHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

/**
 * Read the free-text query to send to the NLP search (free-text/find-by-query)
 * from the editable search bar, EXCLUDING any white-label "Catalog Tier" chips.
 *
 * Catalog Tier is a structured filter: it rides the structured search
 * (search/tags/search) and stays visible in the prompt, but its name must not
 * enter the free-text prompt or the backend errors. In the rendered prompt the
 * tier is the leading segment "{tier names} catalog ..." (see filters-store
 * generatePrompterText), each name marked filter-family-name="Catalog Tier".
 *
 * The source element is not mutated (display stays intact); we clone, strip the
 * marked chips, and — only when a chip was actually removed — drop the now
 * orphaned leading "catalog" connector (and commas left between multiple tiers).
 */
export function getQueryTextExcludingCatalogTier(editableDiv: HTMLElement): string {
  const clone = editableDiv.cloneNode(true) as HTMLElement;
  const chips = clone.querySelectorAll('[filter-family-name="Catalog Tier"]');

  if (chips.length === 0) {
    return (editableDiv.textContent ?? '').trim();
  }

  chips.forEach((chip) => chip.remove());

  const withoutConnector = (clone.textContent ?? '').replace(/^[\s,]*catalog\b\s*/i, '');
  return withoutConnector.replace(/\s+/g, ' ').trim();
}

/**
 * Helper: Find text node at specific position (used by setCursorPosition)
 */
function getTextNodeAtPosition(
  root: Node,
  position: number
): { node: Node; offset: number } | null {
  let currentPosition = 0;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  let node: Node | null = walker.nextNode();
  while (node) {
    const nodeLength = node.textContent?.length || 0;
    if (currentPosition + nodeLength >= position) {
      return {
        node,
        offset: position - currentPosition
      };
    }
    currentPosition += nodeLength;
    node = walker.nextNode();
  }

  return null;
}
